import { useSession } from 'next-auth/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { serviceApi } from '~/features';
import { clearParams, clearParamsSort, parseDate } from '~/shared';
import type { Campaign, CampaignListDTO, CampaignListModelView } from '~/types';

export const GET_CAMPAIGNS_QUERY_KEY = 'GET_CAMPAIGNS_QUERY';

function serializeCampaignStatusParams(
  params: CampaignListDTO
): CampaignListDTO {
  if (!Array.isArray(params.status)) return params;

  return {
    ...params,
    status: JSON.stringify(params.status) as CampaignListDTO['status'],
  };
}

async function getCampaigns(params: CampaignListDTO) {
  const serializedParams = serializeCampaignStatusParams(params);
  let clearedParams;
  if (
    !serializedParams.orderCampaign ||
    !serializedParams.orderDevice ||
    !serializedParams.orderDevice ||
    !serializedParams.orderInitial ||
    !serializedParams.orderFinish
  ) {
    clearedParams = clearParamsSort(serializedParams);
  } else {
    clearedParams = clearParams(serializedParams);
  }

  const response = serviceApi.get<CampaignListModelView>(
    `/Campanha/List?ProjectAdsId=1`,
    {
      params: clearedParams,
    }
  );

  return response;
}

export function useGetCampaigns(params: CampaignListDTO) {
  const queryClient = useQueryClient();
  const { data: session, status } = useSession();
  const manufacturerId = session?.user?.manufacturerId;

  const paramsWithManufacturer: CampaignListDTO = {
    ...params,
    ...(manufacturerId != null && { manufacturerId }),
  };

  const query = useQuery({
    enabled: status !== 'loading',
    queryKey: [GET_CAMPAIGNS_QUERY_KEY, paramsWithManufacturer],
    select: data => {
      const returnData = data?.data;

      return {
        ...returnData,
        list: returnData?.list.map(item => {
          return {
            ...item,
            startDate: parseDate(item.startDate),
            endDate: parseDate(item.endDate),
            createdAt: parseDate(item.createdAt),
            updatedAt: parseDate(item.updatedAt),
            deletedAt: parseDate(item.deletedAt),
          };
        }),
      };
    },
    queryFn: () => getCampaigns(paramsWithManufacturer),
  });

  return Object.assign(query, {
    refetchAll: () =>
      queryClient.refetchQueries({ queryKey: [GET_CAMPAIGNS_QUERY_KEY] }),
  });
}

export type UseGetCampaignsReturn = ReturnType<typeof useGetCampaigns>['data'];

export type UseGetCampaignsReturnList = Array<
  Omit<
    Campaign,
    'startDate' | 'endDate' | 'createdAt' | 'updatedAt' | 'deletedAt'
  > & {
    startDate: Date | null;
    endDate: Date | null;
    createdAt: Date | null;
    updatedAt: Date | null;
    deletedAt: Date | null;
  }
>;
